import React, {useEffect, useRef} from 'react';

const WIND_GRID_SIZE = 10;
const WIND_FETCH_INTERVAL_MS = 60000;

function WindParticles({mapRef, isActive, apiKey}) {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const windFieldRef = useRef({
        base: {deg: 0, speed: 1},
        points: [],
        size: {width: 0, height: 0, ratio: 1}
    });
    const windFetchRef = useRef({
        time: 0,
        lat: null,
        lng: null,
        controller: null
    });
    const resizeObserverRef = useRef(null);

    const updateCanvasSize = (width, height) => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        const ratio = window.devicePixelRatio || 1;
        canvas.width = Math.max(1, Math.round(width * ratio));
        canvas.height = Math.max(1, Math.round(height * ratio));
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        }
        windFieldRef.current.size = {width, height, ratio};
    };

    const buildWindGrid = () => {
        if (!mapRef || !canvasRef.current || !isActive) {
            return;
        }
        const div = mapRef.getDiv();
        if (!div) {
            return;
        }
        const width = div.clientWidth;
        const height = div.clientHeight;
        updateCanvasSize(width, height);

        const rows = WIND_GRID_SIZE;
        const cols = WIND_GRID_SIZE;
        const cellSize = Math.min(width / cols, height / rows);

        const baseDeg = windFieldRef.current.base.deg || 0;
        const baseSpeed = windFieldRef.current.base.speed || 1;
        const points = [];

        for (let r = 0; r < rows; r += 1) {
            for (let c = 0; c < cols; c += 1) {
                const x = (c + 0.5) * (width / cols);
                const y = (r + 0.5) * (height / rows);
                const noise = Math.sin((r * 12.9898 + c * 78.233)) * 15;
                const angle = (baseDeg + noise) * Math.PI / 180;
                const speed = Math.max(12, baseSpeed * 6);

                points.push({x, y, angle, speed, cellSize});
            }
        }

        windFieldRef.current.points = points;
    };

    const drawWind = (timestamp) => {
        const canvas = canvasRef.current;
        if (!canvas || !isActive) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        const {width, height} = windFieldRef.current.size;
        ctx.clearRect(0, 0, width, height);

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 1;

        const time = timestamp / 1000;
        const tail = 6;
        const points = windFieldRef.current.points;

        for (let i = 0; i < points.length; i += 1) {
            const point = points[i];
            const travel = (time * point.speed) % point.cellSize;
            const offset = travel - point.cellSize / 2;
            const dx = Math.cos(point.angle) * offset;
            const dy = Math.sin(point.angle) * offset;
            const x = point.x + dx;
            const y = point.y + dy;

            if (x < -tail || y < -tail || x > width + tail || y > height + tail) {
                continue;
            }

            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x - Math.cos(point.angle) * tail, y - Math.sin(point.angle) * tail);
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(x, y, 1.2, 0, Math.PI * 2);
            ctx.fill();
        }

        animationRef.current = requestAnimationFrame(drawWind);
    };

    const updateWindFromCenter = (lat, lng) => {
        if (!apiKey) {
            return;
        }
        const now = Date.now();
        const last = windFetchRef.current;
        const moved = last.lat === null || last.lng === null || Math.abs(lat - last.lat) > 0.05 || Math.abs(lng - last.lng) > 0.05;
        if (!moved && now - last.time < WIND_FETCH_INTERVAL_MS) {
            return;
        }

        if (last.controller) {
            last.controller.abort();
        }

        const controller = new AbortController();
        windFetchRef.current = {...last, controller};

        fetch(`https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lng}&appid=${apiKey}&units=metric`, {signal: controller.signal})
            .then(response => response.ok ? response.json() : null)
            .then(data => {
                if (!data?.wind) {
                    return;
                }
                const deg = typeof data.wind.deg === 'number' ? data.wind.deg : windFieldRef.current.base.deg;
                const speed = typeof data.wind.speed === 'number' ? data.wind.speed : windFieldRef.current.base.speed;
                windFieldRef.current.base = {deg, speed};
                windFetchRef.current = {time: now, lat, lng, controller: null};
                buildWindGrid();
            })
            .catch(() => {
                if (controller.signal.aborted) {
                    return;
                }
                windFetchRef.current = {time: now, lat, lng, controller: null};
            });
    };

    useEffect(() => {
        if (!mapRef || !window.google?.maps || !isActive) {
            return;
        }
        const maps = window.google.maps;
        const handleIdle = () => {
            const centerPoint = mapRef.getCenter();
            if (centerPoint) {
                updateWindFromCenter(centerPoint.lat(), centerPoint.lng());
            }
            buildWindGrid();
        };

        const idleListener = maps.event.addListener(mapRef, 'idle', handleIdle);
        handleIdle();

        return () => {
            maps.event.removeListener(idleListener);
        };
    }, [mapRef, isActive, apiKey]);

    useEffect(() => {
        if (!mapRef || !isActive) {
            return;
        }
        const div = mapRef.getDiv();
        if (!div || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(() => buildWindGrid());
        observer.observe(div);
        resizeObserverRef.current = observer;
        return () => observer.disconnect();
    }, [mapRef, isActive]);

    useEffect(() => {
        if (!isActive) {
            return;
        }
        animationRef.current = requestAnimationFrame(drawWind);
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [isActive]);

    useEffect(() => {
        if (isActive) {
            return;
        }
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        if (windFetchRef.current.controller) {
            windFetchRef.current.controller.abort();
        }
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
        }
    }, [isActive]);

    if (!isActive) {
        return null;
    }

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                inset: 0,
                zIndex: 1000,
                pointerEvents: 'none'
            }}
        />
    );
}

export default WindParticles;
